/**
 * TriggerModal — Select a trigger to start a new workflow.
 *
 * ORGANISATION:
 *  Triggers are grouped into 6 categories. Each card shows:
 *    - Icon, title, short description
 *    - "When to use" bullet examples
 *    - Status badge: LIVE | SOON | PREMIUM
 *
 * ADDING A NEW TRIGGER:
 *  1. Add its ID to triggerDefs in types.tsx.
 *  2. Add a TriggerDef entry in the CATEGORIES array below.
 *  3. Set live: true only when velaro-messaging actively evaluates the trigger.
 *  4. Document "examples" so admins know when to use it.
 *
 * AUDIT / DIAGNOSTICS:
 *  Selecting a trigger is logged immediately. Subsequent trigger-type changes
 *  inside the editor are logged by TriggerEditor via useActivityLog.
 */
import * as React from "react";
import { useState, useMemo } from "react";
import { Modal, ModalBody, ModalHeader } from "@velaro/velaro-component-lib";

// ── Icon imports ──────────────────────────────────────────────────────────────
import IconMessageChatSquare from "@/icons/IconMessageChatSquare";
import IconMessageCheckSquare from "@/icons/IconMessageCheckSquare";
import IconMessageNotificationSquare from "@/icons/IconMessageNotificationSquare";
import IconOutgoingMessageSquare from "@/icons/IconOutgoingMessageSquare";
import IconUsersPlus from "@/icons/IconUsersPlus";
import IconLink from "@/icons/IconLink";
import IconFileCheck from "@/icons/IconFileCheck";
import IconUpload from "@/icons/IconUpload";
import IconDownload from "@/icons/IconDownload";
import IconAgentTimeout from "@/icons/IconAgentTimeout";
import IconUnresponsiveAgentSquare from "@/icons/IconUnresponsiveAgentSquare";
import IconUnresponsiveVisitorSquare from "@/icons/IconUnresponsiveVisitorSquare";
import IconCalendar from "@/icons/IconCalendar";
import IconTicket from "@/icons/IconTicket";
import IconArrowSquareDownRight from "@/icons/IconArrowSquareDownRight";
import IconArrowRefresh from "@/icons/IconArrowRefresh";
import IconRatings from "@/icons/IconRatings";
import IconEmail from "@/icons/IconEmail";
import IconFlag from "@/icons/IconFlag";
import IconPersonAdd from "@/icons/IconPersonAdd";
import IconPersonTeam from "@/icons/IconPersonTeam";
import IconHistoryTime from "@/icons/IconHistoryTime";
import IconContactsCrm from "@/icons/IconContactsCrm";
import { IconProps } from "@velaro/velaro-component-lib";
import { PremiumTag } from "../../../components/PremiumTag";
import { ComingSoonTag } from "../../../components/ComingSoonTag";

// ── Trigger definition type ───────────────────────────────────────────────────
interface TriggerDef {
  id: string;
  icon: (props: IconProps) => React.JSX.Element;
  title: string;
  description: string;
  /** Short "when would you use this" bullet examples shown in the card. */
  examples: string[];
  live?: boolean;
  comingSoon?: boolean;
  premium?: boolean;
  category: TriggerCategory;
}

type TriggerCategory =
  | "Conversation"
  | "Visitor & Contact"
  | "Agent & Team"
  | "Engagement & Feedback"
  | "Time & Schedule"
  | "Integrations";

// ── Inline SVG icons for triggers that don't have a dedicated Icon file ───────
function IconLabelTag(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
    </svg>
  );
}

function IconRobot(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" {...props}>
      <rect x="3" y="8" width="18" height="13" rx="2" />
      <path strokeLinecap="round" d="M12 3v5M9 3h6" />
      <circle cx="8.5" cy="14" r="1.5" fill="currentColor" />
      <circle cx="15.5" cy="14" r="1.5" fill="currentColor" />
      <path strokeLinecap="round" d="M9 18h6" />
    </svg>
  );
}

function IconClock(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" {...props}>
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 3" />
    </svg>
  );
}

function IconWebhook(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
    </svg>
  );
}

function IconShoppingCart(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
    </svg>
  );
}

function IconDeal(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
    </svg>
  );
}

function IconInboundEmail(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 9v.906a2.25 2.25 0 01-1.183 1.981l-6.478 3.488M2.25 9v.906a2.25 2.25 0 001.183 1.981l6.478 3.488m8.839-2.51l-4.66 2.51m0 0l-1.023.55a2.25 2.25 0 01-2.134 0l-1.022-.55m0 0l-4.661-2.51m16.5 1.615a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V8.844a2.25 2.25 0 011.183-1.98l7.5-4.04a2.25 2.25 0 012.134 0l7.5 4.04a2.25 2.25 0 011.183 1.98V19.5z" />
    </svg>
  );
}

// ── Trigger definitions ───────────────────────────────────────────────────────
const ALL_TRIGGERS: TriggerDef[] = [
  // ── Conversation ──────────────────────────────────────────────────────────
  {
    category: "Conversation",
    id: "conversation_opened",
    icon: IconMessageChatSquare,
    title: "Conversation Opened",
    description: "Fires the moment a new conversation is started — ideal for immediate greetings, intake surveys, or instant bot engagement.",
    examples: [
      "Show a welcome message and capture visitor name/email",
      "Route to the right team based on the page they came from",
      "Launch a pre-chat survey before connecting to an agent",
    ],
    live: true,
  },
  {
    category: "Conversation",
    id: "conversation_resolved",
    icon: IconMessageCheckSquare,
    title: "Conversation Resolved",
    description: "Fires when a conversation is marked resolved or closed. Use to trigger post-conversation automation like CSAT surveys, CRM updates, or follow-up emails.",
    examples: [
      "Push transcript to HubSpot or Dynamics 365",
      "Send a follow-up email 24 hours after resolution",
      "Tag the contact in your CRM with the conversation outcome",
    ],
    live: true,
  },
  {
    category: "Conversation",
    id: "conversation_assigned",
    icon: IconUsersPlus,
    title: "Conversation Assigned",
    description: "Fires when a conversation is assigned to an agent or team. Use to send agent notifications, update CRM assignment records, or log reassignments.",
    examples: [
      "Notify the assigned agent via email or webhook",
      "Update a deal owner in HubSpot when a conversation is routed",
      "Log all reassignments for SLA tracking",
    ],
    live: true,
  },
  {
    category: "Conversation",
    id: "conversation_reopened",
    icon: IconArrowRefresh,
    title: "Conversation Reopened",
    description: "Fires when a previously resolved conversation is reopened by a returning visitor message. Useful for restarting workflows or alerting teams.",
    examples: [
      "Alert the original agent that a resolved case came back",
      "Re-evaluate routing when a visitor replies to a closed chat",
      "Increment a 'reopened count' attribute on the contact",
    ],
    comingSoon: true,
  },
  {
    category: "Conversation",
    id: "label_applied",
    icon: IconLabelTag,
    title: "Label Applied",
    description: "Fires when an agent applies a specific label to a conversation. Configure which labels trigger the workflow in the editor.",
    examples: [
      "Label: 'VIP' → immediately alert the account manager",
      "Label: 'Bug' → create a Jira ticket via HTTP node",
      "Label: 'Lost Deal' → trigger a win-back email sequence",
    ],
    comingSoon: true,
  },
  {
    category: "Conversation",
    id: "bot_handoff_to_agent",
    icon: IconRobot,
    title: "Bot Handed Off to Agent",
    description: "Fires the moment an AI or chatbot transfers the conversation to a live agent. Use to prepare agents with context or send a bridging message.",
    examples: [
      "Send the agent a summary of what the bot collected",
      "Notify a manager when a VIP is being escalated",
      "Start a timer to measure agent response time after handoff",
    ],
    comingSoon: true,
  },

  // ── Visitor & Contact ─────────────────────────────────────────────────────
  {
    category: "Visitor & Contact",
    id: "incoming_message",
    icon: IconMessageNotificationSquare,
    title: "Incoming Message",
    description: "Fires on every new message received from a visitor mid-conversation. Use for keyword detection, sentiment analysis checks, or real-time escalation.",
    examples: [
      "Detect keywords like 'cancel' or 'refund' and route to retention",
      "Log every message to an external analytics system",
      "Send an auto-acknowledgement if no agent has replied within 60s",
    ],
    live: true,
  },
  {
    category: "Visitor & Contact",
    id: "outgoing_message",
    icon: IconOutgoingMessageSquare,
    title: "Outgoing Message",
    description: "Fires whenever a message is sent to a visitor. Use to log communications, detect agent tone with AI, or sync to CRM.",
    examples: [
      "Sync each agent reply to a CRM conversation log",
      "Detect if an agent sent a phone number (policy check)",
    ],
    comingSoon: true,
  },
  {
    category: "Visitor & Contact",
    id: "visitor_opens_page",
    icon: IconLink,
    title: "Visitor Opens a Page",
    description: "Fires when a visitor lands on a specific URL or page pattern. Great for proactive engagement on high-intent pages like pricing or checkout.",
    examples: [
      "Proactively open a chat on the /pricing page after 15 seconds",
      "Offer live help on the checkout page to reduce cart abandonment",
      "Show a targeted greeting based on the product category page",
    ],
    comingSoon: true,
  },
  {
    category: "Visitor & Contact",
    id: "visitor_left",
    icon: IconArrowSquareDownRight,
    title: "Visitor Left the Page",
    description: "Fires when a visitor navigates away or closes the window. Use to trigger exit-intent offers or capture partial form data.",
    examples: [
      "Show a discount offer before a visitor exits the cart page",
      "Log page-exit with duration for analytics",
    ],
    comingSoon: true,
  },
  {
    category: "Visitor & Contact",
    id: "visitor_returns",
    icon: IconArrowSquareDownRight,
    title: "Visitor Returns to Site",
    description: "Fires when a recognised visitor returns to the site. Use to resume conversations, offer personalised help, or re-engage leads.",
    examples: [
      "Resume a conversation from a previous session",
      "Offer a personalised greeting to a returning lead",
      "Notify the account owner a prospect is back on pricing",
    ],
    premium: true,
    comingSoon: true,
  },
  {
    category: "Visitor & Contact",
    id: "form_submitted",
    icon: IconFileCheck,
    title: "Form Submitted",
    description: "Fires when a visitor submits a lead or contact form. Use to trigger instant follow-up, create CRM records, or route to the right team.",
    examples: [
      "Create a contact in HubSpot immediately on form submit",
      "Route the conversation to Sales after a demo request form",
      "Send an automatic confirmation email",
    ],
    comingSoon: true,
  },
  {
    category: "Visitor & Contact",
    id: "contact_created",
    icon: IconPersonAdd,
    title: "Contact Created",
    description: "Fires when a new contact record is created in the system (via chat capture, CSV import, or API). Ideal for onboarding sequences.",
    examples: [
      "Send a welcome email series to every new contact",
      "Enrich the contact record with a third-party data lookup",
      "Add the contact to a specific list for a nurture campaign",
    ],
    comingSoon: true,
  },
  {
    category: "Visitor & Contact",
    id: "contact_updated",
    icon: IconContactsCrm,
    title: "Contact Updated",
    description: "Fires when a contact attribute changes (e.g., plan upgraded, lifecycle stage changed, custom field set). Use to trigger reactive automations.",
    examples: [
      "When plan = 'Enterprise' → assign an enterprise CSM team",
      "When opt-out = true → remove from active campaigns",
      "When NPS score drops below 7 → alert the success team",
    ],
    comingSoon: true,
  },

  // ── Agent & Team ──────────────────────────────────────────────────────────
  {
    category: "Agent & Team",
    id: "unresponsive_agent",
    icon: IconUnresponsiveAgentSquare,
    title: "Unresponsive Agent",
    description: "Fires when an assigned agent hasn't replied within a configured timeout. Automatically escalate, reassign, or send a holding message.",
    examples: [
      "Escalate to a supervisor after 5 minutes of agent silence",
      "Send the visitor an 'We'll be right with you' message",
      "Reassign to another available agent on the same team",
    ],
    comingSoon: true,
  },
  {
    category: "Agent & Team",
    id: "unresponsive_visitor",
    icon: IconUnresponsiveVisitorSquare,
    title: "Unresponsive Visitor",
    description: "Fires when a visitor stops responding during a conversation. Use to send a follow-up prompt, close idle chats, or trigger an email follow-up.",
    examples: [
      "Ask 'Are you still there?' after 3 minutes of visitor silence",
      "Close the conversation automatically after 15 minutes idle",
      "Send a follow-up email if the visitor leaves mid-chat",
    ],
    comingSoon: true,
  },
  {
    category: "Agent & Team",
    id: "agent_timeout",
    icon: IconAgentTimeout,
    title: "Agent Timeout",
    description: "Fires when a conversation has been waiting for an agent longer than the configured queue threshold. Trigger fallbacks or re-routing.",
    examples: [
      "After 10 min in queue → offer a callback instead of waiting",
      "Route to a different team with more capacity",
      "Send the visitor a status update message",
    ],
    comingSoon: true,
  },
  {
    category: "Agent & Team",
    id: "sla_breach",
    icon: IconHistoryTime,
    title: "SLA Threshold Reached",
    description: "Fires when a conversation is approaching or has exceeded your SLA target. Configure the threshold in minutes. Use to escalate before the breach, not after.",
    examples: [
      "Alert a manager 5 minutes before first-response SLA expires",
      "Automatically escalate to Tier 2 at SLA breach",
      "Log all SLA breaches to a reporting webhook",
    ],
    comingSoon: true,
  },
  {
    category: "Agent & Team",
    id: "tier_escalated",
    icon: IconPersonTeam,
    title: "Routing Tier Escalated",
    description: "Fires when the routing engine advances to the next agent tier because no one in the previous tier accepted. Use to notify managers or adjust the conversation priority.",
    examples: [
      "Notify a senior agent pool when escalated to Tier 2",
      "Send the visitor a longer expected wait time message",
      "Flag for management review when Tier 3 is reached",
    ],
    comingSoon: true,
  },

  // ── Engagement & Feedback ─────────────────────────────────────────────────
  {
    category: "Engagement & Feedback",
    id: "csat_submitted",
    icon: IconRatings,
    title: "CSAT Rating Submitted",
    description: "Fires when a visitor submits a CSAT survey response after a conversation. Filter by rating range — e.g., fire only for 1–2 star ratings to trigger a service recovery workflow.",
    examples: [
      "Rating 1–2 → alert a manager and create a recovery task",
      "Rating 5 → ask the customer for a review or referral",
      "Any rating → push score to HubSpot contact timeline",
    ],
    live: true,
  },
  {
    category: "Engagement & Feedback",
    id: "email_opened",
    icon: IconEmail,
    title: "Campaign Email Opened",
    description: "Fires when a recipient opens one of your broadcast emails. Use to trigger a timely follow-up or personalise the next touchpoint.",
    examples: [
      "Email opened → start a live chat proactive greeting for 24h",
      "Opened but no link clicked after 3 days → send a reminder",
      "Log open events to HubSpot contact engagement timeline",
    ],
    live: true,
  },
  {
    category: "Engagement & Feedback",
    id: "email_link_clicked",
    icon: IconEmail,
    title: "Campaign Link Clicked",
    description: "Fires when a recipient clicks any tracked link in a broadcast email. Use to identify high-intent leads and trigger immediate sales outreach.",
    examples: [
      "Clicked pricing link → immediately notify the sales rep",
      "Clicked demo link but didn't book → send a calendar invite",
      "Route to Sales team if a prospect clicks 3+ links in one email",
    ],
    live: true,
  },
  {
    category: "Engagement & Feedback",
    id: "file_uploaded",
    icon: IconUpload,
    title: "File Uploaded",
    description: "Fires when a visitor uploads a file during a conversation. Use to trigger document processing workflows or route to a specialist.",
    examples: [
      "Route to a document specialist when an ID is uploaded",
      "Trigger a webhook to scan the file for compliance",
    ],
    comingSoon: true,
  },
  {
    category: "Engagement & Feedback",
    id: "file_downloaded",
    icon: IconDownload,
    title: "File Downloaded",
    description: "Fires when a visitor downloads a file from the chat. Useful for tracking content engagement.",
    examples: [
      "Log download events to an analytics platform",
      "Follow up with pricing info after a spec sheet is downloaded",
    ],
    comingSoon: true,
  },

  // ── Time & Schedule ───────────────────────────────────────────────────────
  {
    category: "Time & Schedule",
    id: "scheduled",
    icon: IconClock,
    title: "Scheduled (Cron)",
    description: "Fires on a repeating schedule defined by a cron expression — run reports, send batch emails, or trigger daily automations at a set time.",
    examples: [
      "Every Monday 9am → send weekly conversation summary email",
      "Daily at midnight → archive contacts with no activity in 90 days",
      "First of month → generate and email a performance report",
    ],
    comingSoon: true,
  },

  // ── Integrations ──────────────────────────────────────────────────────────
  {
    category: "Integrations",
    id: "appointment_scheduled",
    icon: IconCalendar,
    title: "Appointment Scheduled",
    description: "Fires when an appointment is booked through the Velaro booking widget. Use to confirm, prepare, or notify stakeholders.",
    examples: [
      "Send a confirmation SMS immediately after booking",
      "Create a calendar invite and send to both parties",
      "Alert the assigned agent 15 minutes before the meeting",
    ],
    premium: true,
    comingSoon: true,
  },
  {
    category: "Integrations",
    id: "service_ticket_created",
    icon: IconTicket,
    title: "Service Ticket Created",
    description: "Fires when a support ticket is opened — either in Velaro or via a connected helpdesk integration. Start SLA timers, notify agents, or push to external systems.",
    examples: [
      "Auto-assign a ticket based on product area tag",
      "Send confirmation email to the customer with ticket number",
      "Push high-priority tickets to PagerDuty via webhook",
    ],
    premium: true,
    comingSoon: true,
  },
  {
    category: "Integrations",
    id: "order_status_changed",
    icon: IconShoppingCart,
    title: "Order Status Changed",
    description: "Fires when a BigCommerce or Shopify order transitions to a new status (shipped, delivered, refunded, etc.). Trigger proactive customer communications.",
    examples: [
      "Order shipped → send a tracking number via SMS or chat",
      "Refund issued → open a conversation to confirm resolution",
      "Delivery failed → notify customer and offer rescheduling",
    ],
    premium: true,
    comingSoon: true,
  },
  {
    category: "Integrations",
    id: "deal_stage_changed",
    icon: IconDeal,
    title: "Deal Stage Changed",
    description: "Fires when a HubSpot or Dynamics 365 deal moves to a configured pipeline stage. Sync your CRM lifecycle events with Velaro automation.",
    examples: [
      "Deal → 'Proposal Sent' → send a follow-up chat proactive",
      "Deal → 'Closed Won' → trigger onboarding workflow",
      "Deal → 'Closed Lost' → start a re-engagement sequence",
    ],
    premium: true,
    comingSoon: true,
  },
  {
    category: "Integrations",
    id: "inbound_email_received",
    icon: IconInboundEmail,
    title: "Inbound Email Received",
    description: "Fires when your connected inbound email channel receives a new message. Route email tickets, auto-reply, or create CRM records.",
    examples: [
      "New support email → auto-create a ticket and acknowledge",
      "Route emails by subject keyword to the right team",
      "Extract contact data and update your CRM on first email",
    ],
    comingSoon: true,
  },
  {
    category: "Integrations",
    id: "webhook_received",
    icon: IconWebhook,
    title: "Webhook Received",
    description: "Fires when an external system POSTs to a secure Velaro webhook URL. The ultimate escape hatch — connect any system that can send an HTTP request.",
    examples: [
      "Stripe payment confirmed → trigger onboarding workflow",
      "GitHub PR merged → notify a customer about their fix",
      "PagerDuty incident opened → auto-message the affected account",
    ],
    premium: true,
    comingSoon: true,
  },
];

// ── Category metadata ─────────────────────────────────────────────────────────
const CATEGORY_ORDER: TriggerCategory[] = [
  "Conversation",
  "Visitor & Contact",
  "Agent & Team",
  "Engagement & Feedback",
  "Time & Schedule",
  "Integrations",
];

const CATEGORY_DESCRIPTIONS: Record<TriggerCategory, string> = {
  "Conversation":          "Fires based on the lifecycle of a conversation",
  "Visitor & Contact":     "Fires based on visitor behavior or contact data changes",
  "Agent & Team":          "Fires when agents or routing conditions are breached",
  "Engagement & Feedback": "Fires based on survey responses, email activity, and file transfers",
  "Time & Schedule":       "Fires on a recurring schedule independent of conversations",
  "Integrations":          "Fires based on events in connected CRM, e-commerce, or external systems",
};

// ── Component ─────────────────────────────────────────────────────────────────
interface Props {
  onClose: () => void;
  onSelect(triggerId: string): void;
}

export default function TriggerModal({ onClose, onSelect }: Props) {
  const [query, setQuery] = useState("");
  const [openCategories, setOpenCategories] = useState<Set<TriggerCategory>>(
    new Set(CATEGORY_ORDER)
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return ALL_TRIGGERS;
    const q = query.toLowerCase();
    return ALL_TRIGGERS.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.examples.some((e) => e.toLowerCase().includes(q)) ||
        t.category.toLowerCase().includes(q)
    );
  }, [query]);

  const grouped = useMemo(() => {
    const map = new Map<TriggerCategory, TriggerDef[]>();
    for (const cat of CATEGORY_ORDER) map.set(cat, []);
    for (const t of filtered) map.get(t.category)?.push(t);
    return map;
  }, [filtered]);

  function toggleCategory(cat: TriggerCategory) {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  }

  const liveCount  = ALL_TRIGGERS.filter((t) => t.live).length;
  const totalCount = ALL_TRIGGERS.length;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <Modal maxWidth="max-w-6xl" show={true}>
        <ModalHeader title="Select a Trigger" onClose={onClose} />
        <ModalBody>
          <div className="-mt-3">
            {/* Header copy + stats */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-gray-500 max-w-xl">
                Triggers are events or conditions that kick-start your workflow.
                Choose one below — then build out the actions that follow.
              </p>
              <span className="text-xs text-gray-400 whitespace-nowrap ml-4">
                {liveCount} live &bull; {totalCount} total
              </span>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Search triggers…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search triggers"
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-cornflower-blue-400"
              />
              <svg
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <path strokeLinecap="round" d="M21 21l-4.35-4.35" />
              </svg>
            </div>

            <hr className="mb-4 border-gray-200" />

            {/* Category sections */}
            <div className="max-h-[60vh] overflow-y-auto pr-1 space-y-4">
              {CATEGORY_ORDER.map((cat) => {
                const triggers = grouped.get(cat) ?? [];
                if (triggers.length === 0) return null;
                const isOpen = openCategories.has(cat);

                return (
                  <div key={cat}>
                    {/* Category header */}
                    <button
                      type="button"
                      onClick={() => toggleCategory(cat)}
                      className="w-full flex items-center justify-between py-1.5 text-left group"
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? "Collapse" : "Expand"} ${cat} triggers`}
                    >
                      <div>
                        <span className="font-semibold text-slate-700 text-sm">{cat}</span>
                        <span className="ml-2 text-xs text-gray-400">{CATEGORY_DESCRIPTIONS[cat]}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">{triggers.length}</span>
                        <svg
                          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>

                    {/* Trigger cards */}
                    {isOpen && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
                        {triggers.map((trigger) => (
                          <TriggerCard
                            key={trigger.id}
                            trigger={trigger}
                            onClick={() => {
                              if (!trigger.comingSoon) {
                                onSelect(trigger.id);
                              }
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {filtered.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-8">
                  No triggers match &ldquo;{query}&rdquo;
                </p>
              )}
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

// ── TriggerCard ───────────────────────────────────────────────────────────────
function TriggerCard({
  trigger,
  onClick,
}: {
  trigger: TriggerDef;
  onClick?: () => void;
}) {
  const isDisabled = !trigger.live && (trigger.comingSoon || trigger.premium);

  return (
    <div
      role={isDisabled ? undefined : "button"}
      tabIndex={isDisabled ? undefined : 0}
      onClick={isDisabled ? undefined : onClick}
      onKeyDown={isDisabled ? undefined : (e) => e.key === "Enter" && onClick?.()}
      aria-label={isDisabled ? undefined : `Select trigger: ${trigger.title}`}
      className={`group p-4 border rounded-lg flex flex-col gap-2 transition-all ${
        isDisabled
          ? "bg-gray-50 border-gray-200 cursor-not-allowed"
          : "bg-white border-gray-200 hover:border-cornflower-blue-500 hover:shadow-sm cursor-pointer"
      }`}
    >
      {/* Icon + badge row */}
      <div className="flex items-center justify-between">
        <trigger.icon
          className={`w-6 h-6 ${
            isDisabled
              ? "stroke-gray-300 fill-gray-300 text-gray-300"
              : "stroke-slate-500 fill-slate-500 text-slate-500 group-hover:stroke-cornflower-blue-500 group-hover:fill-cornflower-blue-500 group-hover:text-cornflower-blue-500"
          }`}
        />
        <div className="flex items-center gap-1">
          {trigger.live && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
              LIVE
            </span>
          )}
          {trigger.comingSoon && !trigger.live && <ComingSoonTag />}
          {trigger.premium && !trigger.comingSoon && !trigger.live && <PremiumTag />}
        </div>
      </div>

      {/* Title + description */}
      <div>
        <div className={`font-semibold text-sm leading-tight ${
          isDisabled ? "text-gray-500" : "text-slate-800 group-hover:text-cornflower-blue-600"
        }`}>
          {trigger.title}
        </div>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed line-clamp-2">
          {trigger.description}
        </p>
      </div>

      {/* Examples — visible on hover for live triggers */}
      {!isDisabled && (
        <ul className="hidden group-hover:block mt-1 space-y-0.5">
          {trigger.examples.slice(0, 2).map((ex, i) => (
            <li key={i} className="flex items-start gap-1 text-[11px] text-cornflower-blue-500">
              <span className="mt-0.5 shrink-0">›</span>
              <span>{ex}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Coming soon note */}
      {trigger.comingSoon && !trigger.live && (
        <p className="text-[11px] text-gray-400 italic mt-auto">
          Coming soon — already planned and tracked.
        </p>
      )}
    </div>
  );
}
